import { useEffect, useRef, useState } from "react";
import {
  Wrench,
  Music2,
  Trees,
  ShoppingBag,
  Droplets,
  Flame,
  Home as HomeIcon,
  Sparkles,
  BookOpen,
} from "lucide-react";
import { api } from "@/lib/api";

// ---------------------------------------------------------------
// Vance · Industrial Strike Library
// Scrolling marquee of eight trade-specific e-book covers.
// Click → industry tag persisted (sessionStorage + backend) so
// Amanda's concierge personalises her greeting on the next open.
// ---------------------------------------------------------------

export const STRIKE_INDUSTRIES = [
  { id: "auto",        label: "Auto Shop",         accent: "#F5C842", icon: Wrench,     tagline: "Bay bookings, dead reviews, chased down." },
  { id: "band",        label: "Band",              accent: "#A78BFA", icon: Music2,     tagline: "Merch drops, fan loops, tour money captured." },
  { id: "landscaper",  label: "Landscaper",        accent: "#34D399", icon: Trees,      tagline: "Seasonal sprints, no-shows sealed shut." },
  { id: "boutique",    label: "Boutique",          accent: "#F472B6", icon: ShoppingBag, tagline: "DM to door — every window shopper tracked." },
  { id: "plumbing",    label: "Plumbing",          accent: "#60A5FA", icon: Droplets,   tagline: "After-hours calls captured, dispatched, done." },
  { id: "hvac",        label: "HVAC",              accent: "#F97316", icon: Flame,      tagline: "Peak-season overflow routed like a utility." },
  { id: "roofing",     label: "Roofing",           accent: "#EF4444", icon: HomeIcon,   tagline: "Storm-chase leads locked before the tarp lands." },
  { id: "cleaning",    label: "Cleaning Services", accent: "#22D3EE", icon: Sparkles,   tagline: "Recurring routes, subs, and re-books on autopilot." },
];

const STORAGE_KEY = "grid_industry_tag";
const STORAGE_META_KEY = "grid_industry_tag_meta";

export function readStoredIndustryTag() {
  try {
    return sessionStorage.getItem(STORAGE_KEY) || "";
  } catch (_) {
    return "";
  }
}

export default function StrikeLibrary() {
  const [tagged, setTagged] = useState(() => readStoredIndustryTag());
  const trackRef = useRef(null);
  const [paused, setPaused] = useState(false);

  // Duplicate the list once so the CSS marquee loops seamlessly.
  const items = [...STRIKE_INDUSTRIES, ...STRIKE_INDUSTRIES];

  const onPick = async (industry) => {
    setTagged(industry.label);
    try {
      sessionStorage.setItem(STORAGE_KEY, industry.label);
      sessionStorage.setItem(
        STORAGE_META_KEY,
        JSON.stringify({ id: industry.id, label: industry.label, at: Date.now() }),
      );
    } catch (_) { /* ignore */ }
    try {
      await api.post("/concierge/tag-industry", {
        industry: industry.label,
        route: typeof window !== "undefined" ? window.location.pathname : "/",
        source: "strike-library",
      });
    } catch (_) { /* non-fatal */ }
  };

  return (
    <section
      className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6 lg:px-10 py-14 sm:py-16"
      data-testid="strike-library"
    >
      <div className="flex items-center gap-3 mb-3 justify-center">
        <div className="hud-line w-10" />
        <span className="hud-label text-gold flex items-center gap-2">
          <BookOpen size={12} /> Industrial Strike Library
        </span>
        <div className="hud-line w-10" />
      </div>
      <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl leading-tight tracking-tight text-center max-w-3xl mx-auto">
        Pick your <span className="text-gold italic">trade</span>. Amanda picks up the file.
      </h2>
      <p className="mt-4 text-slate-400 text-sm sm:text-base text-center max-w-2xl mx-auto leading-relaxed">
        Eight verticals, eight strike briefings. Tap the cover that fits your
        shop — Amanda will already know what business you run when the concierge opens.
      </p>

      {tagged && (
        <div
          className="mt-6 mx-auto max-w-xl rounded-sm border border-emerald-400/40 bg-emerald-400/[0.06] px-4 py-2.5 text-center"
          data-testid="strike-library-tagged-banner"
        >
          <span className="text-[10px] uppercase tracking-[0.32em] text-emerald-300 font-mono-aviation">
            Trade Locked ·{" "}
          </span>
          <span className="text-white text-sm font-display">{tagged}</span>
          <span className="text-slate-400 text-xs ml-2">— Amanda has your file.</span>
        </div>
      )}

      <div
        className="mt-10 relative overflow-hidden rounded-sm border border-white/10"
        style={{
          background:
            "linear-gradient(180deg, rgba(212,175,55,0.05) 0%, rgba(6,11,20,0.55) 100%)",
          maskImage:
            "linear-gradient(90deg, transparent, black 6%, black 94%, transparent)",
          WebkitMaskImage:
            "linear-gradient(90deg, transparent, black 6%, black 94%, transparent)",
        }}
        onMouseEnter={() => setPaused(true)}
        onMouseLeave={() => setPaused(false)}
        data-testid="strike-library-marquee"
      >
        <div
          ref={trackRef}
          className="flex gap-4 py-6 px-4"
          style={{
            width: "max-content",
            animation: `strike-marquee 42s linear infinite`,
            animationPlayState: paused ? "paused" : "running",
          }}
        >
          {items.map((it, i) => (
            <StrikeCover
              key={`${it.id}-${i}`}
              item={it}
              onClick={() => onPick(it)}
              active={tagged === it.label}
            />
          ))}
        </div>
      </div>

      <div className="mt-4 text-center text-[10px] uppercase tracking-[0.28em] text-slate-500 font-mono-aviation">
        Hover to pause · Click a cover to tag your industry
      </div>

      <style>{`
        @keyframes strike-marquee {
          0%   { transform: translateX(0); }
          100% { transform: translateX(-50%); }
        }
      `}</style>
    </section>
  );
}

function StrikeCover({ item, onClick, active }) {
  const Icon = item.icon;
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={`strike-cover-${item.id}`}
      aria-label={`Tag industry: ${item.label}`}
      className="group relative shrink-0 w-[220px] sm:w-[240px] rounded-sm border overflow-hidden transition-all text-left"
      style={{
        borderColor: active ? item.accent : "rgba(255,255,255,0.10)",
        background:
          `linear-gradient(180deg, ${item.accent}18 0%, rgba(6,11,20,0.9) 60%)`,
        boxShadow: active
          ? `0 0 26px -6px ${item.accent}80`
          : "0 12px 24px -14px rgba(0,0,0,0.6)",
        transform: active ? "translateY(-2px)" : "translateY(0)",
      }}
    >
      <div
        className="absolute inset-x-0 top-0 h-0.5"
        style={{ backgroundColor: item.accent, opacity: 0.75 }}
      />
      {/* Cover art — thick spine + faux page block */}
      <div className="relative h-40 flex items-center justify-center overflow-hidden">
        <div
          className="absolute left-3 top-4 bottom-4 w-1.5 rounded-sm"
          style={{ background: `linear-gradient(180deg, ${item.accent}, ${item.accent}55)` }}
        />
        <svg viewBox="0 0 200 160" className="w-full h-full" aria-hidden>
          <rect x="26" y="18" width="150" height="126" rx="2"
            fill="rgba(6,11,20,0.65)" stroke={item.accent} strokeWidth="1.2" />
          {/* Faint page lines */}
          <g stroke={item.accent} strokeOpacity="0.28" strokeWidth="0.8">
            <line x1="42" y1="46"  x2="150" y2="46" />
            <line x1="42" y1="60"  x2="140" y2="60" />
            <line x1="42" y1="74"  x2="146" y2="74" />
            <line x1="42" y1="120" x2="120" y2="120" />
            <line x1="42" y1="130" x2="130" y2="130" />
          </g>
          {/* Emblem centre glow */}
          <circle cx="100" cy="94" r="28" fill={item.accent} fillOpacity="0.10" />
          <circle cx="100" cy="94" r="20" fill="none" stroke={item.accent} strokeWidth="1.2" />
        </svg>
        <div
          className="absolute w-12 h-12 rounded-sm border flex items-center justify-center"
          style={{
            borderColor: `${item.accent}90`,
            color: item.accent,
            background: "rgba(6,11,20,0.75)",
            top: "50%", left: "50%", transform: "translate(-50%, -50%)",
          }}
        >
          <Icon size={22} />
        </div>
        <div
          className="absolute top-2 left-2 px-2 py-0.5 rounded-sm text-[9px] uppercase tracking-[0.28em] font-mono-aviation"
          style={{
            borderColor: `${item.accent}80`,
            color: item.accent,
            border: "1px solid",
            background: "rgba(6,11,20,0.85)",
          }}
        >
          E-Book
        </div>
      </div>
      <div className="p-4 border-t border-white/10">
        <div
          className="font-display text-white text-base leading-tight group-hover:text-gold transition-colors"
        >
          {item.label}
        </div>
        <p className="mt-1.5 text-[11px] leading-relaxed text-slate-400 min-h-[32px]">
          {item.tagline}
        </p>
        <div
          className="mt-3 inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.28em] font-mono-aviation"
          style={{ color: item.accent }}
        >
          {active ? "Locked · Amanda has it" : "Tap to Tag"}
        </div>
      </div>
    </button>
  );
}
